/**
 * A picked colour (`#rrggbb`, the workbench's Appearance swatches and its custom picker) as the accent tokens take
 * it: the OKLCH hue, and the share of the theme's accent chroma it carries. The tokens keep their own lightness per
 * theme and role, so a pick changes the hue everywhere without any button losing its contrast; a black, white or
 * grey pick (no chroma) becomes a neutral accent rather than an arbitrary hue (Dustin, 2026-10-20: one accent colour).
 */
export interface AccentOfColor {
  hue: number;
  /** 0-1: the colour's OKLCH chroma over Paige blue's, capped at 1. */
  chroma: number;
}

/** Paige blue's chroma in the tokens (light 0.17, dark 0.15): a colour as saturated takes all of it. */
const FULL_CHROMA = 0.15;

const channel = (hex: string, at: number): number => {
  const c = parseInt(hex.slice(at, at + 2), 16) / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** Björn Ottosson's OKLab from linear sRGB, then polar. Null for anything that is not `#rrggbb`. */
export function accentOfColor(color: string): AccentOfColor | null {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return null;
  const [r, g, b] = [channel(color, 1), channel(color, 3), channel(color, 5)];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const chroma = Math.hypot(a, bb);
  const hue = ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
  return {
    hue: Math.round(hue),
    chroma: Math.min(1, Math.round((chroma / FULL_CHROMA) * 100) / 100),
  };
}
